import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Copy, Check, Shield } from "lucide-react";
import { toast } from "sonner";
import { SERVER_IP } from "@/lib/api";
import { sfx } from "@/lib/sounds";
import SoundSettings from "./SoundSettings";
import StreamerToggle from "./StreamerToggle";
import AuthButton from "./AuthButton";
import NotificationsBell from "./NotificationsBell";
import Logo from "./Logo";

import GlobalSearch from "./GlobalSearch";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/", label: "Home" },
  { to: "/servers", label: "Servers" },
  { to: "/store", label: "Store" },
  { to: "/wallet", label: "Wallet" },
  { to: "/pluse", label: "Pluse" },
  { to: "/creators", label: "Creators" },
  { to: "/leaderboards", label: "Leaderboards" },
  { to: "/events", label: "Events" },
  { to: "/builds", label: "Builds" },
  { to: "/about", label: "About" },
];

export default function Navbar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const copyIP = async () => {
    try {
      await navigator.clipboard.writeText(SERVER_IP);
      setCopied(true);
      sfx.copy();
      toast.success("Server IP copied", { description: SERVER_IP });
      setTimeout(() => setCopied(false), 1600);
    } catch { sfx.error(); toast.error("Copy failed"); }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding,box-shadow] duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10 py-2 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
          : "backdrop-blur-md bg-black/40 border-b border-transparent py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-2">
        <Link to="/" data-testid="nav-logo" onClick={() => sfx.click()} className="flex items-center gap-2 shrink-0 min-w-0">
          <Logo className="h-10 sm:h-12 md:h-14" />
          <div className="hidden 2xl:flex flex-col leading-tight">

          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 shrink">
          {links.slice(0, 6).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => sfx.click()}
              onMouseEnter={() => sfx.hover()}
              className={({ isActive }) =>
                `px-2.5 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full transition-colors whitespace-nowrap ${
                  isActive ? "bg-[#FFBF00] text-black" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="nav-copy-ip"
            onClick={copyIP}
            className="hidden xl:inline-flex items-center gap-2 px-4 py-2 rounded-full glass hover:border-[#FFBF00]/40 text-sm font-mono transition-colors"
          >
            <span className="w-2 h-2 rounded-full bg-green-500 pulse-dot" />
            <span>{SERVER_IP}</span>
            {copied ? <Check size={14} className="text-[#FFBF00]" /> : <Copy size={14} />}
          </button>
          <SoundSettings />
          <StreamerToggle compact />
          <GlobalSearch />
          <NotificationsBell />
          <AuthButton />
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              onClick={() => sfx.click()}
              className="hidden md:inline-flex p-2 rounded-full hover:bg-white/5 border border-transparent hover:border-white/10 text-white/70"
              aria-label="Admin"
            >
              <Shield size={16} />
            </Link>
          )}
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 rounded-md hover:bg-white/5"
            onClick={() => { setOpen((o) => !o); sfx.menu(); }}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-xl glass p-3" data-testid="mobile-nav">
          <div className="grid grid-cols-2 gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => { setOpen(false); sfx.click(); }}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm rounded-md ${
                    isActive ? "bg-[#FFBF00]/10 text-[#FFBF00]" : "text-white/80 hover:bg-white/5"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink
              to="/contact"
              data-testid="mobile-nav-link-contact"
              onClick={() => { setOpen(false); sfx.click(); }}
              className="px-3 py-2 text-sm rounded-md text-white/80 hover:bg-white/5"
            >
              Contact
            </NavLink>
            <NavLink to="/orders" data-testid="mobile-nav-link-orders" onClick={() => { setOpen(false); sfx.click(); }} className="px-3 py-2 text-sm rounded-md text-white/80 hover:bg-white/5">My Orders</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 text-sm rounded-md text-white/80 hover:bg-white/5">Admin</NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
